import React from "react";

import {
  normalizeManuscriptContent,
} from "../utils/childrensBookManuscriptContent";


function boundedIndentation(
  value
) {
  const indentation =
    Number(value) || 0;

  return Math.min(
    96,
    Math.max(
      0,
      Math.round(
        indentation / 15
      )
    )
  );
}


function blockFontSize(
  block,
  proofPresentation = false
) {
  if (
    block.type !==
    "heading"
  ) {
    return proofPresentation
      ? 14
      : 16;
  }

  if (block.level === 1) {
    return proofPresentation
      ? 22
      : 28;
  }

  if (block.level === 2) {
    return proofPresentation
      ? 19
      : 23;
  }

  return proofPresentation
    ? 16
    : 19;
}


function listMarker(
  block,
  orderedIndex
) {
  if (
    block.type !==
    "list_item"
  ) {
    return "";
  }

  return block.list?.type ===
    "ordered"
    ? `${orderedIndex}.`
    : "•";
}


function fallbackText(
  value
) {
  return typeof value ===
    "string"
    ? value
    : "";
}


export default function ChildrensBookManuscriptReadOnly({
  content,
  fallbackText: fallbackValue = "",
  lang = "en",
  presentation = "",
  flowAnchorBlockId = "",
  flowContent = null,
  flowInsertions = [],
  embeddedFlow = false,
}) {
  const isFR =
    lang === "fr";

  const proofPresentation =
    String(
      presentation
    ).toLowerCase() ===
    "publication_proof";

  const normalized =
    normalizeManuscriptContent(
      content
    );

  const blocks =
    normalized.ok &&
    normalized.content &&
    Array.isArray(
      normalized.content.blocks
    )
      ? normalized.content.blocks
      : [];

  const requestedFlowAnchor =
    String(
      flowAnchorBlockId || ""
    ).trim();

  const requestedFlowInsertions =
    [
      ...(
        flowContent &&
        requestedFlowAnchor
          ? [
              {
                anchorBlockId:
                  requestedFlowAnchor,
                content:
                  flowContent,
              },
            ]
          : []
      ),
      ...(
        Array.isArray(
          flowInsertions
        )
          ? flowInsertions
          : []
      ),
    ]
      .map(
        (item) => ({
          anchorBlockId:
            String(
              item
                ?.anchorBlockId ||
              ""
            ).trim(),
          content:
            item?.content ??
            null,
        })
      )
      .filter(
        (item) =>
          item.anchorBlockId &&
          item.content != null
      );

  const validParagraphIds =
    new Set(
      blocks
        .filter(
          (block) =>
            block.type ===
              "paragraph"
        )
        .map(
          (block) =>
            String(
              block.id || ""
            )
        )
    );

  const activeFlowInsertions =
    requestedFlowInsertions
      .filter(
        (item) =>
          validParagraphIds
            .has(
              item
                .anchorBlockId
            )
      );

  const flowEnabled =
    activeFlowInsertions
      .length > 0;

  if (!blocks.length) {
    return (
      <div
        data-testid="manuscript-read-only"
        data-structured="false"
        style={{
          whiteSpace:
            "pre-wrap",
          overflowWrap:
            "anywhere",
          fontSize:
            proofPresentation
              ? 14
              : undefined,
          lineHeight:
            proofPresentation
              ? 1.5
              : undefined,
        }}
      >
        {fallbackText(
          fallbackValue
        )}
      </div>
    );
  }

  let orderedIndex = 0;
  let figureFlowOpen = false;

  return (
    <div
      data-testid="manuscript-read-only"
      data-structured="true"
      data-manuscript-flow-anchor={
        flowEnabled
          ? (
              activeFlowInsertions[
                0
              ]
                ?.anchorBlockId ||
              ""
            )
          : ""
      }
      data-manuscript-flow-anchor-count={
        activeFlowInsertions.length
      }
      style={{
        /*
         * Embedded writing surfaces remain one continuous manuscript stream.
         * Stand-alone Figure-flow projections may establish their own BFC,
         * but Textbook embedded flow must stay fragmentable across working
         * pages just like the proven Solo / Group Open writing surface.
         */
        display:
          embeddedFlow
            ? "block"
            : flowEnabled
              ? "flow-root"
              : "grid",
        gap:
          embeddedFlow ||
          flowEnabled
            ? undefined
            : (
                proofPresentation
                  ? 1
                  : 3
              ),
      }}
    >
      {blocks.map(
        (
          block,
          index
        ) => {
          if (
            block.type ===
              "list_item" &&
            block.list?.type ===
              "ordered"
          ) {
            orderedIndex += 1;
          } else {
            orderedIndex = 0;
          }

          const flowContentsAtBlock =
            activeFlowInsertions
              .filter(
                (item) =>
                  item.anchorBlockId ===
                  String(
                    block.id || ""
                  )
              );

          const startsFigureFlow =
            flowEnabled &&
            block.type ===
              "paragraph" &&
            flowContentsAtBlock
              .length > 0;

          if (startsFigureFlow) {
            figureFlowOpen =
              true;
          }

          const paragraphInFigureFlow =
            figureFlowOpen &&
            block.type ===
              "paragraph";

          const clearsFigureFlow =
            figureFlowOpen &&
            block.type !==
              "paragraph";

          if (clearsFigureFlow) {
            figureFlowOpen =
              false;
          }

          if (
            block.type ===
            "page_break"
          ) {
            return (
              <div
                key={block.id}
                role="separator"
                aria-label={
                  isFR
                    ? "Saut de page"
                    : "Page break"
                }
                data-manuscript-block-type="page_break"
                style={{
                  display: "flex",
                  clear:
                    clearsFigureFlow
                      ? "both"
                      : undefined,
                  alignItems:
                    "center",
                  gap: 10,
                  margin:
                    proofPresentation
                      ? "8px 0"
                      : "14px 0",
                  color: "#64748b",
                  fontSize:
                    proofPresentation
                      ? 10
                      : 12,
                  fontWeight: 800,
                  letterSpacing:
                    "0.04em",
                  textTransform:
                    "uppercase",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flex: 1,
                    height: 1,
                    background:
                      "#cbd5e1",
                  }}
                />

                {isFR
                  ? "Saut de page"
                  : "Page break"}

                <span
                  aria-hidden="true"
                  style={{
                    flex: 1,
                    height: 1,
                    background:
                      "#cbd5e1",
                  }}
                />
              </div>
            );
          }

          const marker =
            listMarker(
              block,
              orderedIndex
            );

          return (
            <React.Fragment
              key={block.id}
            >
              {startsFigureFlow
                ? flowContentsAtBlock
                    .map(
                      (
                        insertion,
                        flowIndex
                      ) => (
                        <React.Fragment
                          key={
                            `${insertion.anchorBlockId}-flow-${flowIndex}`
                          }
                        >
                          {
                            insertion
                              .content
                          }
                        </React.Fragment>
                      )
                    )
                : null}

              <div
              data-manuscript-block-id={
                block.id
              }
              data-manuscript-block-type={
                block.type
              }
              style={{
                display:
                  paragraphInFigureFlow
                    ? "block"
                    : "grid",
                clear:
                  clearsFigureFlow
                    ? "both"
                    : undefined,
                gridTemplateColumns:
                  paragraphInFigureFlow
                    ? undefined
                    : (
                        marker
                          ? "28px minmax(0, 1fr)"
                          : "minmax(0, 1fr)"
                      ),
                gap:
                  paragraphInFigureFlow
                    ? undefined
                    : 8,
                marginLeft:
                  boundedIndentation(
                    block
                      .indentation
                      ?.left
                  ),
                padding:
                  block.type ===
                  "blockquote"
                    ? (
                        proofPresentation
                          ? "7px 9px"
                          : "10px 12px"
                      )
                    : (
                        proofPresentation
                          ? "2px 0"
                          : "4px 0"
                      ),
                borderLeft:
                  block.type ===
                  "blockquote"
                    ? "4px solid #94a3b8"
                    : "none",
                background:
                  block.type ===
                  "blockquote"
                    ? "#f8fafc"
                    : "transparent",
                borderRadius:
                  block.type ===
                  "blockquote"
                    ? 8
                    : 0,
              }}
            >
              {marker ? (
                <span
                  aria-hidden="true"
                  style={{
                    paddingTop:
                      proofPresentation
                        ? 4
                        : 8,
                    fontWeight: 800,
                    textAlign:
                      "right",
                    color: "#475569",
                  }}
                >
                  {marker}
                </span>
              ) : null}

              <div
                style={{
                  minHeight:
                    proofPresentation
                      ? (
                          block.type ===
                          "heading"
                            ? 32
                            : 24
                        )
                      : (
                          block.type ===
                          "heading"
                            ? 42
                            : 34
                        ),
                  padding:
                    proofPresentation
                      ? "3px 5px"
                      : "7px 9px",
                  textAlign:
                    block.alignment ||
                    "left",
                  fontSize:
                    blockFontSize(
                      block,
                      proofPresentation
                    ),
                  fontWeight:
                    block.type ===
                    "heading"
                      ? 800
                      : 500,
                  lineHeight:
                    proofPresentation
                      ? 1.5
                      : 1.65,
                  whiteSpace:
                    "pre-wrap",
                  overflowWrap:
                    "anywhere",
                }}
              >
                {(
                  Array.isArray(
                    block.runs
                  ) &&
                  block.runs.length
                    ? block.runs
                    : [
                        {
                          text: "",
                        },
                      ]
                ).map(
                  (
                    run,
                    runIndex
                  ) => (
                    <span
                      key={`${block.id}-run-${runIndex}`}
                      data-bold={
                        Boolean(
                          run.bold
                        )
                      }
                      data-italic={
                        Boolean(
                          run.italic
                        )
                      }
                      data-underline={
                        Boolean(
                          run
                            .underline
                        )
                      }
                      data-highlight={
                        Boolean(
                          run
                            .highlight
                        )
                      }
                      data-font-family={
                        run
                          .fontFamily ||
                        ""
                      }
                      data-font-size={
                        run.fontSize ||
                        ""
                      }
                      style={{
                        fontWeight:
                          run.bold
                            ? 800
                            : "inherit",
                        fontStyle:
                          run.italic
                            ? "italic"
                            : "normal",
                        textDecoration:
                          run
                            .underline
                            ? "underline"
                            : "none",
                        backgroundColor:
                          run
                            .highlight
                            ? "#fef08a"
                            : "transparent",
                        fontFamily:
                          run
                            .fontFamily ||
                          "inherit",
                        fontSize:
                          run.fontSize
                            ? `${run.fontSize}pt`
                            : "inherit",
                      }}
                    >
                      {run.text}
                    </span>
                  )
                )}
              </div>
            </div>
            </React.Fragment>
          );
        }
      )}
    </div>
  );
}
